/**
 * G8 research library on the device: sealed `research` entities (written by the agent through
 * `viko_research_save`, or by the user) listed per project and per user. Decrypted and validated
 * here; the relay only stores ciphertext. A malformed or hostile row is skipped, never shown.
 */
import { useQuery } from '@tanstack/react-query'
import { ResearchRecord } from '@viko/protocol'
import { useBackend } from './backend.js'

export interface ResearchEntry extends ResearchRecord {
  id: string
}
interface Row {
  id: string
  value: Record<string, unknown>
}

export async function loadResearch(store: { list(kind: 'research'): Promise<Row[]> }) {
  const out: ResearchEntry[] = []
  for (const row of await store.list('research')) {
    const r = ResearchRecord.safeParse(row.value)
    if (r.success) out.push({ ...r.data, id: row.id })
  }
  return out.sort((a, b) => b.createdAt - a.createdAt || a.id.localeCompare(b.id))
}

export function sourceHost(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return ''
  }
}

export function filterResearch(
  all: readonly ResearchEntry[],
  f: { projectId?: string; scope?: 'user'; query?: string },
): ResearchEntry[] {
  const q = f.query?.trim().toLowerCase()
  return all.filter((r) => {
    if (f.projectId && r.projectId !== f.projectId) return false
    if (f.scope === 'user' && r.scope !== 'user') return false
    if (!q) return true
    return [
      r.title,
      r.summary,
      r.query ?? '',
      ...r.tags,
      ...r.sources.flatMap((s) => [s.title, sourceHost(s.url)]),
    ].some((t) => t.toLowerCase().includes(q))
  })
}

export const researchForConversation = (all: readonly ResearchEntry[], barkId: string) =>
  all.filter((r) => r.conversation?.barkId === barkId)

/** A user-saved entry (e.g. "keep this" in voice mode). Validated before it is sealed. */
export async function saveResearch(
  store: { create(kind: 'research', value: Record<string, unknown>): Promise<{ id: string }> },
  i: Pick<ResearchRecord, 'scope' | 'title' | 'summary' | 'sources'> &
    Partial<Pick<ResearchRecord, 'projectId' | 'tags' | 'conversation' | 'query'>>,
  now = Date.now(),
): Promise<{ id: string }> {
  const value = ResearchRecord.parse({
    v: 1,
    tags: [],
    ...i,
    by: 'user',
    createdAt: now,
    updatedAt: now,
  })
  return store.create('research', value)
}

/** The account's research library (every project and the user's own). */
export function useResearch() {
  const b = useBackend()
  const ents = b.entities
  return useQuery({
    queryKey: ['research', !!ents],
    queryFn: () => (ents ? loadResearch(ents as never) : Promise.resolve([])),
    enabled: !!ents,
    staleTime: 30_000,
  })
}
